import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowClockwise,
  Cards,
  ChartLineUp,
  CheckCircle,
  Funnel,
  Student,
  Warning,
} from "@phosphor-icons/react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import AcademicDomainChart from "../components/AcademicDomainChart";
import MedcofEngagementPanel from "../components/MedcofEngagementPanel";

const fmt = (value, digits = 2, suffix = "") => `${Number(value || 0).toLocaleString("pt-BR", {
  minimumFractionDigits: digits,
  maximumFractionDigits: digits,
})}${suffix}`;

const shortDate = (value) => {
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

function Delta({ value, suffix = "%", inverse = false }) {
  if (value === null || value === undefined) return <span className="text-muted-foreground">sem base anterior</span>;
  const positive = Number(value) >= 0;
  const good = inverse ? !positive : positive;
  return (
    <span className={good ? "text-[hsl(var(--success))]" : "text-destructive"}>
      {positive ? "+" : ""}{fmt(value, 2, suffix)} vs. período anterior
    </span>
  );
}

function Metric({ icon: Icon, label, value, hint, delta, inverse = false }) {
  return (
    <div className="border border-border bg-card p-5 min-h-[150px] flex flex-col" data-testid={`dashboard-metric-${label}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="label-eyebrow">{label}</span>
        <Icon size={20} weight="duotone" className="text-primary" />
      </div>
      <div className="font-display text-4xl font-black tracking-tight mt-4">{value}</div>
      <div className="text-xs font-mono-data mt-auto pt-3 text-muted-foreground">
        {delta !== undefined ? <Delta value={delta} inverse={inverse} suffix={label === "cartões" ? "%" : " p.p."} /> : hint}
      </div>
    </div>
  );
}

function ExamProgressionTooltip({ active, payload }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div className="border border-border bg-background p-3 shadow-lg text-xs min-w-56">
      <div className="font-mono-data font-bold">{point.exam_name}</div>
      <div className="text-muted-foreground mt-1">{new Date(`${point.date}T12:00:00`).toLocaleDateString("pt-BR")}</div>
      <div className="font-display text-2xl font-black text-primary mt-2">{fmt(point.average_score, 2, "%")}</div>
      <div className="text-muted-foreground mt-1">{point.students} aluno(s) · {point.cards} cartão(ões)</div>
    </div>
  );
}

function HealthBar({ label, value, detail, tone = "primary" }) {
  const width = Math.max(0, Math.min(100, Number(value || 0)));
  const color = tone === "success" ? "bg-[hsl(var(--success))]" : tone === "warning" ? "bg-[hsl(var(--warning))]" : "bg-primary";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-semibold">{label}</span>
        <span className="font-mono-data font-bold">{fmt(width, 2, "%")}</span>
      </div>
      <div className="h-2 bg-muted mt-2 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${width}%` }} /></div>
      <div className="text-xs text-muted-foreground mt-1.5">{detail}</div>
    </div>
  );
}

function ComparisonTable({ title, eyebrow, rows }) {
  return (
    <section className="border border-border bg-card overflow-hidden">
      <div className="p-5 border-b border-border">
        <div className="label-eyebrow">{eyebrow}</div>
        <h2 className="font-display text-2xl font-black tracking-tight mt-1">{title}</h2>
      </div>
      <div className="grid grid-cols-[1fr_70px_100px_90px] gap-3 px-4 py-3 bg-muted/30 border-b border-border label-eyebrow">
        <div>Nome</div><div>Cartões</div><div>Média</div><div>Revisões</div>
      </div>
      {(rows || []).slice(0, 8).map((row) => (
        <div key={row.id || row.name} className="grid grid-cols-[1fr_70px_100px_90px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center text-sm">
          <div className="font-semibold truncate" title={row.name}>{row.name}</div>
          <div className="font-mono-data">{row.cards}</div>
          <div className="font-mono-data font-bold">{fmt(row.average_score, 2, "%")}</div>
          <div className={row.pending_reviews ? "font-mono-data text-destructive" : "font-mono-data text-muted-foreground"}>{row.pending_reviews}</div>
        </div>
      ))}
      {!rows?.length && <div className="p-10 text-center label-eyebrow">sem dados no período</div>}
    </section>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ days: 30, exam_id: "", class_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: filters.days };
      if (filters.exam_id) params.exam_id = filters.exam_id;
      if (filters.class_id) params.class_id = filters.class_id;
      const response = await api.get("/analytics/dashboard", { params });
      setData(response.data);
    } catch (error) {
      toast.error("Falha ao carregar o dashboard: " + (error?.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const trend = useMemo(() => (data?.trend || []).map((item) => ({
    ...item,
    label: shortDate(item.date),
  })), [data?.trend]);
  const progression = data?.exam_progression || [];
  const summary = data?.summary || {};
  const comparisons = data?.comparisons || {};
  const omr = data?.omr || {};
  const filterOptions = data?.filters || {};

  return (
    <main className="max-w-[1400px] mx-auto px-8 py-10" data-testid="dashboard-page">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-6 mb-8">
        <div>
          <div className="label-eyebrow">{data?.release || "SAKI/Xm"} · PostgreSQL nativo e pipeline OMR</div>
          <h1 className="font-display text-5xl font-black tracking-tighter mt-2">Visão operacional e pedagógica</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-3xl">
            Acompanhe volume, desempenho acadêmico, revisões e qualidade da leitura OMR dentro do seu escopo de acesso.
          </p>
        </div>
        <Button variant="outline" className="rounded-none shrink-0" onClick={load} disabled={loading}>
          <ArrowClockwise size={15} weight="bold" className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      <section className="border border-border bg-card p-4 mb-6 flex flex-col lg:flex-row lg:items-center gap-4" data-testid="dashboard-filters">
        <div className="flex items-center gap-2 label-eyebrow shrink-0"><Funnel size={16} weight="bold" /> filtros</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((days) => (
            <button key={days} type="button" onClick={() => setFilters((value) => ({ ...value, days }))}
              className={`border px-3 py-2 text-xs font-mono-data uppercase tracking-wider ${filters.days === days ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground"}`}>
              {days} dias
            </button>
          ))}
        </div>
        <select value={filters.exam_id} onChange={(event) => setFilters((value) => ({ ...value, exam_id: event.target.value }))}
          className="border border-border bg-background px-3 py-2 text-sm min-w-52">
          <option value="">Todas as provas</option>
          {(filterOptions.exam_options || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <select value={filters.class_id} onChange={(event) => setFilters((value) => ({ ...value, class_id: event.target.value }))}
          className="border border-border bg-background px-3 py-2 text-sm min-w-52">
          <option value="">Todas as turmas</option>
          {(filterOptions.class_options || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        {(filters.exam_id || filters.class_id) && (
          <button type="button" onClick={() => setFilters((value) => ({ ...value, exam_id: "", class_id: "" }))}
            className="text-xs font-mono-data uppercase tracking-wider hover:underline lg:ml-auto">limpar seleção</button>
        )}
      </section>

      {loading && !data ? (
        <div className="border border-border p-20 text-center label-eyebrow">calculando indicadores…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <Metric icon={Cards} label="cartões" value={summary.cards || 0} delta={comparisons.cards_change_percent} />
            <Metric icon={ChartLineUp} label="nota média" value={fmt(summary.average_score, 2, "%")} delta={comparisons.score_change_points} />
            <Metric icon={Warning} label="revisões pendentes" value={summary.pending_reviews || 0} delta={comparisons.review_rate_change_points} inverse />
            <Metric icon={Student} label="alunos identificados" value={fmt(summary.identification_rate, 2, "%")} hint={`${summary.identified_students || 0} cartão(ões) vinculados`} />
          </div>

          <MedcofEngagementPanel showTimelines />

          <section className="border border-border bg-card p-5 mb-6" data-testid="dashboard-exam-progression">
            <div className="label-eyebrow">progressão temporal</div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mt-1">
              <h2 className="font-display text-2xl font-black tracking-tight">Acertos prova a prova</h2>
              <div className="text-xs text-muted-foreground font-mono-data">Até 24 provas realizadas · somente correções com gabarito válido</div>
            </div>
            {progression.length ? (
              <div className="overflow-x-auto mt-4">
                <div className="h-[350px]" style={{ width: "100%", minWidth: `${Math.max(640, progression.length * 56)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={progression} margin={{ top: 12, right: 22, left: -8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" interval={0} tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontSize: 10 }} unit="%" />
                      <Tooltip content={<ExamProgressionTooltip />} />
                      <ReferenceLine y={60} stroke="hsl(var(--success))" strokeDasharray="5 5" label={{ value: "60%", fontSize: 10, fill: "hsl(var(--success))" }} />
                      <Line type="monotone" dataKey="average_score" name="Média de acertos" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4, fill: "hsl(var(--background))", strokeWidth: 3 }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground mt-4">Nenhuma prova realizada com correção válida.</div>}
          </section>

          <AcademicDomainChart
            domains={data?.academic_domains}
            eyebrow="mapa curricular geral"
            title="Acertos e erros por disciplina e conteúdo"
            description="Compare as áreas de maior domínio e os pontos de atenção de todos os alunos dentro dos filtros selecionados acima."
          />

          <div className="grid xl:grid-cols-[1.7fr_1fr] gap-6 mb-6">
            <section className="border border-border bg-card p-5">
              <div className="label-eyebrow">ritmo de processamento</div>
              <h2 className="font-display text-2xl font-black tracking-tight mt-1">Cartões por dia</h2>
              <div className="h-[330px] mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <defs><linearGradient id="cardsFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35}/><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip labelFormatter={(_, payload) => payload?.[0]?.payload?.date ? new Date(`${payload[0].payload.date}T12:00:00`).toLocaleDateString("pt-BR") : ""} />
                    <Area type="monotone" dataKey="cards" name="Cartões" stroke="hsl(var(--primary))" fill="url(#cardsFill)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="border border-border bg-card p-5">
              <div className="label-eyebrow">distribuição de desempenho</div>
              <h2 className="font-display text-2xl font-black tracking-tight mt-1">Faixas de nota</h2>
              <div className="h-[330px] mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.score_distribution || []} margin={{ top: 8, right: 5, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => [`${value} cartão(ões)`, "Quantidade"]} />
                    <Bar dataKey="count" name="Cartões" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>

          <div className="grid xl:grid-cols-2 gap-6 mb-6">
            <ComparisonTable eyebrow="comparação acadêmica" title="Provas com atividade" rows={data?.exams} />
            <ComparisonTable eyebrow="comparação de grupos" title="Turmas com atividade" rows={data?.classes} />
          </div>

          <div className="grid xl:grid-cols-[1.2fr_1fr] gap-6">
            <section className="border border-border bg-card p-6">
              <div className="label-eyebrow">saúde da leitura</div>
              <h2 className="font-display text-2xl font-black tracking-tight mt-1">OMR e AI Consensus</h2>
              <div className="space-y-6 mt-6">
                <HealthBar label="Acordo forte entre leitores" value={omr.consensus_agreement} detail={`${omr.confirmed_answers || 0} respostas confirmadas · ${omr.recovered_answers || 0} recuperadas`} tone="success" />
                <HealthBar label="Confiança média da leitura" value={omr.average_confidence} detail={`${omr.cards_with_consensus || 0} cartão(ões) com telemetria V39`} />
                <HealthBar label="Automação sem revisão pendente" value={100 - Number(summary.review_rate || 0)} detail={`${summary.pending_reviews || 0} revisão(ões) pendentes no período`} tone={summary.pending_reviews ? "warning" : "success"} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mt-7">
                {[["confirmadas", omr.confirmed_answers], ["recuperadas", omr.recovered_answers], ["fallbacks", omr.compatibility_fallbacks], ["sem consenso", omr.unresolved_answers]].map(([label, value]) => (
                  <div key={label} className="bg-card p-3"><div className="label-eyebrow">{label}</div><div className="font-display text-2xl font-black mt-1">{value || 0}</div></div>
                ))}
              </div>
            </section>

            <section className="border border-border bg-card p-6">
              <div className="label-eyebrow">leitura executiva</div>
              <h2 className="font-display text-2xl font-black tracking-tight mt-1">Pontos de atenção</h2>
              <div className="space-y-3 mt-6">
                {(data?.insights || []).map((item) => (
                  <div key={item.code} className={`border p-4 flex items-start gap-3 ${item.severity === "ok" ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5" : "border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/5"}`}>
                    {item.severity === "ok" ? <CheckCircle size={20} weight="fill" className="text-[hsl(var(--success))] shrink-0" /> : <Warning size={20} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />}
                    <div className="text-sm">{item.text}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground font-mono-data mt-6 pt-4 border-t border-border">
                Atualizado em {data?.generated_at ? new Date(data.generated_at).toLocaleString("pt-BR") : "—"}. Os dados respeitam suas permissões de prova e turma.
              </div>
            </section>
          </div>
        </>
      )}
    </main>
  );
}
